import { Inject, Injectable } from '@angular/core';
import { deleteDoc, doc, docData, getFirestore } from '@angular/fire/firestore';
import { Storage } from '@angular/fire/storage';
import { COMMONS_ENV, HttpService, NetworkService, ObservableQueryService } from '@damofire/data-angular-commons';
import { logger, HttpUtils, ID_FIELD_OPTIONS, MEDIAS_COLLECTION, Query, SearchResult, updateBatch, WW_ISSUES_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService, AuthenticationService } from '@damofire/data-security';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { FirebaseIssueCfgService } from '../config/firebase-issue-cfg.service';
import { TypedDesQuery } from '../des.dto';
import { FirebaseObservableIssueService } from './firebase-issue-observable.service';
import { Issue, IssueMedia } from './issue.dto';
import { IssueService } from './issue.service';

@Injectable()
export class FirebaseIssueService extends IssueService {
  constructor(
    private readonly dtoSigner: AuthDtoSignerService,
    protected readonly issueCfgService: FirebaseIssueCfgService,
    protected readonly http: HttpService,
    protected readonly appAuthService: AuthenticationService,
    protected readonly networkService: NetworkService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    private readonly observableIssueService: FirebaseObservableIssueService,
    private readonly queryService: ObservableQueryService,
    protected readonly storage: Storage,
  ) {
    super(issueCfgService, appAuthService, environment, http, networkService, storage);
  }

  async getImage(media: IssueMedia, type: 'thumb' | 'web' | 'original'): Promise<string> {
    return this.resolveMediaImageRemote(media, type);
  }

  async deleteMedia(media: IssueMedia): Promise<void> {
    const { issue, division, _id } = media;
    await HttpUtils.post<void>(this.environment.appConfig.fns, 'ww-issueMediaDelete', { data: { issueId: issue._id, divisionId: division?._id, _id } });
  }

  async updateMedia(media: IssueMedia): Promise<void> {
    await this.dtoSigner.update(media, `${WW_ISSUES_COLLECTION}/${media.issue?._id}/${MEDIAS_COLLECTION}/${media._id}`);
  }

  async uploadImage(issue: Issue, filename: string, bytes: string, mediaId?: string, annotations?: unknown[]): Promise<void> {
    const image = bytes.replace(/data:image\/[a-zA-Z]+;base64,/, '');
    return HttpUtils.post<void>(this.environment.appConfig.fns, 'ww-issueMediaCreate', {
      data: { issue, division: issue.division, organization: issue.organization, filename, image, _id: mediaId, annotations },
    });
  }

  async create(divisionId: string, issue: Issue): Promise<Issue> {
    const createdIssues = await this.createBatch(divisionId, [issue]);
    return createdIssues[0];
  }

  async createBatch(divisionId: string, issues: Issue[]): Promise<Issue[]> {
    return HttpUtils.post(this.environment.appConfig.fns, 'ww-issueCreateBatch', { data: { issuesDetails: issues } });
  }

  async search(id: string, sQuery: TypedDesQuery): Promise<SearchResult<Issue>> {
    return firstValueFrom(this.observableIssueService.search(id, sQuery));
  }

  protected async dbUpdate(divisionId: string, dbIssue: Issue, beforeIssue: Issue): Promise<void> {
    logger.info('Updating issue', { id: dbIssue._id, category: 'issue' });
    dbIssue['audit.lastModifiedByType'] = await this.getUserType();
    await this.dtoSigner.update(dbIssue, `${WW_ISSUES_COLLECTION}/${dbIssue._id}`);
  }

  async updateBatch(issues: Issue[]): Promise<void> {
    for (const issue of issues) {
      issue.audit.lastModifiedByType = await this.getUserType();
    }
    return updateBatch(WW_ISSUES_COLLECTION, issues);
  }

  async delete(divisionId: string, issues: Issue[]): Promise<void> {
    for (const issue of issues) {
      await deleteDoc(doc(getFirestore(), `${WW_ISSUES_COLLECTION}/${issue._id}`));
    }
  }

  async get(divisionId: string, id: string): Promise<Issue> {
    return firstValueFrom(docData<Issue>(doc(getFirestore(), `${WW_ISSUES_COLLECTION}/${id}`), ID_FIELD_OPTIONS));
  }

  async buildCustomItemList(divisionId: string, urlSearchString: string): Promise<string[]> {
    const params = new URLSearchParams(urlSearchString);
    if (!params.has('weeklyToReview')) {
      return [];
    }

    const query: Query = {
      filters: { 'division._id': divisionId },
      filtersOps: [{ field: 'reviewDate', op: '<=', value: moment().endOf('isoWeek').endOf('day').toDate() }],
    };

    const results = await this.queryService.collection<Issue>(WW_ISSUES_COLLECTION, query);
    return results.result.map((i) => i._id);
  }
}
